import { TrophyLineIcon } from 'assets/icons';
import Button from 'components/button';
import { COMMERCIAL_ROLE } from 'helper/roles';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import { GOAL_METRICS } from './statsGoals';

const isCount = (value) => /^\d+$/.test(String(value).replace(/\s/g, ''));
const toCount = (value) => Number(String(value).replace(/\s/g, ''));
const cellKey = (id, key) => `${id}.${key}`;

/**
 * « Objectifs des commerciaux » of the admin console, the trophies of the
 * « Suivi-Progression » sheet: for each commercial, the calls and the
 * appointments booked a week, the appointments held, the sales and the
 * revenue a month, which the statistics show beside their figures. Nothing
 * is saved before « Enregistrer ». `members`: the team of the CRM
 * ({ id, firstname, lastname, role }).
 */
const StatsGoalsCard = ({ members }) => {
  const { text } = useText('adminConsole');
  const { call, ready } = useCrmContext();
  const [goals, setGoals] = useState(null);
  const [failed, setFailed] = useState(false);
  // The values typed, by commercial and goal, until they are saved.
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const commercials = members.filter(
    (member) => member.role === COMMERCIAL_ROLE,
  );

  const load = useCallback(() => {
    setFailed(false);
    call(crmApi.getStatsGoals).then(setGoals, () => setFailed(true));
  }, [call]);
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  const savedOf = (id, key) =>
    String(goals.people?.[id]?.[key] ?? goals.defaults[key]);
  const valueOf = (id, key) => draft[cellKey(id, key)] ?? savedOf(id, key);
  const changed = goals
    ? Object.keys(draft).filter((cell) => {
        const [id, key] = cell.split('.');
        return draft[cell] !== savedOf(id, key);
      })
    : [];
  const valid = changed.every((cell) => isCount(draft[cell]));

  const save = () => {
    const people = {};
    changed.forEach((cell) => {
      const [id, key] = cell.split('.');
      people[id] = { ...people[id], [key]: toCount(draft[cell]) };
    });
    setSaving(true);
    call(crmApi.saveStatsGoals, { people }).then(
      (saved) => {
        setSaving(false);
        setGoals(saved);
        setDraft({});
        toast.success(text('goalsSaved'));
      },
      () => {
        setSaving(false);
        toast.error(text('goalsSaveError'));
      },
    );
  };

  return (
    <section className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white">
      <div className="flex flex-col gap-0.5 px-4 py-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
          <TrophyLineIcon wh={18} color="currentColor" />
          {text('goalsTitle')}
        </h2>
        <p className="text-sm text-gray-500">{text('goalsText')}</p>
      </div>
      {failed && (
        <div className="flex items-center gap-3 border-t border-gray-100 px-4 py-4 text-sm text-gray-600">
          {text('goalsLoadError')}
          <Button color="SECONDARY" size="SM" onClick={load}>
            {text('goalsRetry')}
          </Button>
        </div>
      )}
      {!failed && goals && !commercials.length && (
        <p className="border-t border-gray-100 px-4 py-4 text-sm text-gray-500">
          {text('goalsNone')}
        </p>
      )}
      {!failed && goals && commercials.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-gray-100">
                <th className="bg-gray-50 py-3.5 pl-4 pr-2 text-left text-sm font-medium text-gray-700">
                  {text('goalsCommercial')}
                </th>
                {GOAL_METRICS.map(({ key }) => (
                  <th
                    key={key}
                    className="bg-gray-50 px-2 py-3.5 text-right text-xs font-medium text-gray-700 last:pr-4"
                  >
                    {text(`goalsColumn_${key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {commercials.map((member) => {
                const name =
                  `${member.firstname ?? ''} ${member.lastname ?? ''}`.trim();
                return (
                  <tr
                    key={member.id}
                    className="border-b border-gray-50 last:border-b-0"
                  >
                    <td className="whitespace-nowrap py-3 pl-4 pr-2 text-gray-900">
                      {name}
                    </td>
                    {GOAL_METRICS.map(({ key }) => {
                      const value = valueOf(member.id, key);
                      return (
                        <td key={key} className="px-2 py-3 last:pr-4">
                          <input
                            type="text"
                            inputMode="numeric"
                            aria-label={text('goalsInputLabel', {
                              goal: text(`goalsName_${key}`),
                              name,
                            })}
                            value={value}
                            onChange={(event) =>
                              setDraft((current) => ({
                                ...current,
                                [cellKey(member.id, key)]: event.target.value,
                              }))
                            }
                            className={`ml-auto block h-9 rounded-md border bg-white px-3 text-right text-sm text-gray-900 focus:outline-none ${key === 'revenue' ? 'w-28' : 'w-20'} ${isCount(value) ? 'border-gray-200 focus:border-yellow-500' : 'border-red-500'}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {changed.length > 0 && (
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-4 py-3">
          <span className="mr-auto text-xs text-gray-500">
            {valid ? text('goalsUnsaved') : text('goalsInvalid')}
          </span>
          <Button color="SECONDARY" size="SM" onClick={() => setDraft({})}>
            {text('goalsCancel')}
          </Button>
          <Button
            color="PRIMARY"
            size="SM"
            disabled={!valid || saving}
            onClick={save}
          >
            {text('goalsSave')}
          </Button>
        </div>
      )}
    </section>
  );
};

export default StatsGoalsCard;
